import { RefObject, useEffect, useState } from 'react';

type Args = {
  isVertical: boolean;
  labelRef: RefObject<HTMLElement | null>;
  containerRef: RefObject<HTMLElement | null>;
  /** Triggers re-measure when these change (e.g. selectedOptions.length, lockActiveState). */
  deps?: ReadonlyArray<unknown>;
};

/**
 * Vertical-mode-only label observer. Tracks two things:
 *  - `isMultiRow`: whether the label has wrapped past one row (drives fake-border
 *    border-radius — pill vs 16px rounded rect).
 *  - `--custom-select-label-offset-top`: the label's static offset within the
 *    container, applied as a CSS variable so the fake-border can start at the label
 *    top (skipping the header) without wrapping the header above it.
 */
const useVerticalLabelMetrics = ({ isVertical, labelRef, containerRef, deps = [] }: Args) => {
  const [isMultiRow, setIsMultiRow] = useState(false);

  useEffect(() => {
    if (!isVertical) {
      setIsMultiRow(false);
      return;
    }
    const el = labelRef.current;
    const containerEl = containerRef.current;
    if (!el || !containerEl) return;

    const compute = () => {
      const rowHeight = parseFloat(getComputedStyle(el).getPropertyValue('--custom-select-control-height')) || 42;
      // .label.vertical has 8px vertical padding + chip wrapper, so a single row's
      // scrollHeight already exceeds rowHeight. Use 1.5× as the multi-row threshold.
      setIsMultiRow(el.scrollHeight > rowHeight * 1.5);

      // Track the label's static top offset within the container (offsetTop chain —
      // stable across scroll) so the fake-border can start exactly where the label
      // starts, with `bottom: 0` deriving height.
      let offsetTop = 0;
      let node: HTMLElement | null = el;
      while (node && node !== containerEl) {
        offsetTop += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      containerEl.style.setProperty('--custom-select-label-offset-top', `${offsetTop}px`);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVertical, labelRef, containerRef, ...deps]);

  return isMultiRow;
};

export default useVerticalLabelMetrics;
